import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { DEFAULT_RANGE_SIZE, OVERSCAN, READY_DELAY, SCROLL_BUFFER } from './constants';
import {
   calculateInitialRange,
   calculateOffset,
   calculateScrollPosition,
   clampRange,
   defaultKeyExtractor,
   findItemIndex,
   findVisibleRangeEnd,
   findVisibleRangeStart,
   getItemHeightValue,
   isAtBottom,
   isHeaderItemDefault,
   resolveSmartTargetVisualTop,
   waitForAnimationFrame,
   waitForTimeout,
} from './helpers';

/**
 * useVirtualization
 *
 * Core engine shared by FlatList and SectionList.
 * Accepts a flat `items` array — SectionList is responsible for flattening its
 * sections before calling this hook.
 *
 * Returns everything needed to render a virtualised container:
 *   visibleRange, isReady, offsetTop, offsetBottom,
 *   measureItem, scrollToKey, scrollToIndex, jumpToBottom,
 *   takeScrollSnapshot, clearScrollSnapshot
 */
const useVirtualization = ({
   items = [],
   containerRef,
   itemHeight = 80,
   overscan = OVERSCAN,
   getItemHeight: getItemHeightFn,
   keyExtractor = defaultKeyExtractor,
   onScroll,
   inverted = false,
   initialScrollKey = null,
   initialScrollIndex = null,
   initialScrollBlock = 'center',
   isHeaderItem = isHeaderItemDefault,
}) => {
   // ─── State ─────────────────────────────────────────────────────────────────

   const [visibleRange, setVisibleRange] = useState(() => calculateInitialRange(items, initialScrollKey, keyExtractor));
   const [isReady, setIsReady] = useState(!initialScrollKey);

   // ─── Refs ──────────────────────────────────────────────────────────────────

   const itemHeightsRef = useRef(new Map());
   const visibleRangeRef = useRef(visibleRange);
   const targetIndexRef = useRef(null);
   const isProgrammaticScrollRef = useRef(false);
   const scrollTimeoutRef = useRef(null);
   const isRestoringScrollRef = useRef(false);
   const hasInitializedRef = useRef(false);
   const previousItemsLengthRef = useRef(items.length);
   const restorationPrevLengthRef = useRef(items.length);
   const lastTrackedKeyRef = useRef(
      (() => {
         if (items.length === 0) return null;
         if (inverted) return keyExtractor(items[0], 0);
         return keyExtractor(items[items.length - 1], items.length - 1);
      })(),
   );
   const scrollSnapshotRef = useRef(null);
   const offsetTopRef = useRef(0);
   const offsetBottomRef = useRef(0);
   // Holds cancel fn for any in-flight scroll RAF loop.
   // Called when a new scrollToKey starts so only the latest resolves.
   const cancelActiveScrollRef = useRef(null);
   const lastInitializedScrollKeyRef = useRef(initialScrollKey);
   const lastInitializedScrollIndexRef = useRef(initialScrollIndex);

   // Keep the ref in sync so callbacks always read current range without deps
   useEffect(() => {
      visibleRangeRef.current = visibleRange;
   }, [visibleRange]);

   // ─── Visible Range ─────────────────────────────────────────────────────────

   const calculateVisibleRange = useCallback(
      (force = false) => {
         const container = containerRef?.current;
         if (!container || items.length === 0) {
            const range = {
               start: 0,
               end: items.length === 0 ? 0 : Math.min(DEFAULT_RANGE_SIZE, items.length),
            };
            if (force || visibleRangeRef.current.start !== range.start || visibleRangeRef.current.end !== range.end) {
               setVisibleRange(range);
            }
            return;
         }
         if ((isProgrammaticScrollRef.current || isRestoringScrollRef.current) && !force) return;

         const containerHeight = container.clientHeight;
         if (containerHeight <= 0) return;

         const scrollTop = inverted ? Math.abs(container.scrollTop) : Math.max(0, container.scrollTop);
         const visibleBottom = scrollTop + containerHeight;

         const start = findVisibleRangeStart(
            items,
            scrollTop,
            overscan,
            itemHeightsRef,
            keyExtractor,
            getItemHeightFn,
            itemHeight,
         );
         const end = findVisibleRangeEnd(
            items,
            visibleBottom,
            overscan,
            itemHeightsRef,
            keyExtractor,
            getItemHeightFn,
            itemHeight,
         );
         const clamped = clampRange(start, end, items.length);

         if (force || visibleRangeRef.current.start !== clamped.start || visibleRangeRef.current.end !== clamped.end) {
            setVisibleRange(clamped);
         }
      },
      [items, itemHeight, getItemHeightFn, overscan, containerRef, keyExtractor, inverted],
   );

   // ─── Scroll Event Listener ─────────────────────────────────────────────────

   useEffect(() => {
      const container = containerRef?.current;
      if (!container) return;

      calculateVisibleRange();

      const handleScroll = () => {
         requestAnimationFrame(() => {
            calculateVisibleRange();
            onScroll?.({
               scrollTop: container.scrollTop,
               scrollHeight: container.scrollHeight,
               clientHeight: container.clientHeight,
            });
            isAtBottom(container, inverted);
         });
      };

      container.addEventListener('scroll', handleScroll, { passive: true });
      window.addEventListener('resize', calculateVisibleRange);
      return () => {
         container.removeEventListener('scroll', handleScroll);
         window.removeEventListener('resize', calculateVisibleRange);
      };
   }, [calculateVisibleRange, containerRef, onScroll, inverted]);

   useEffect(() => {
      return () => {
         if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
      };
   }, []);

   // ─── Scroll Snapshot (scroll-position preservation across load-more) ───────

   const clearScrollSnapshot = useCallback(() => {
      scrollSnapshotRef.current = null;
   }, []);

   const takeScrollSnapshot = useCallback(
      direction => {
         const container = containerRef?.current;
         if (!container || items.length === 0) return;

         const scrollTop = inverted ? Math.abs(container.scrollTop) : container.scrollTop;
         let currentOffset = 0;
         let firstVisibleIndex = 0;
         let chosenAnchorKey = null;

         for (let i = 0; i < items.length; i++) {
            const item = items[i];
            const key = keyExtractor(item, i);
            const height = getItemHeightValue(item, i, itemHeightsRef, keyExtractor, getItemHeightFn, itemHeight);

            if (isHeaderItem(key) && items.length > i + 1) {
               currentOffset += height;
               continue;
            }
            if (currentOffset + height > scrollTop || i === items.length - 1) {
               firstVisibleIndex = i;
               chosenAnchorKey = key;
               break;
            }
            currentOffset += height;
         }

         scrollSnapshotRef.current = {
            scrollTop,
            firstVisibleIndex,
            firstVisibleKey: chosenAnchorKey || keyExtractor(items[firstVisibleIndex], firstVisibleIndex),
            offsetToFirstVisible: currentOffset,
            itemsLength: items.length,
            timestamp: Date.now(),
            direction,
         };
      },
      [containerRef, items, itemHeight, getItemHeightFn, keyExtractor, isHeaderItem, inverted],
   );

   // ─── Scroll Restoration (after prepend / load-more) ───────────────────────

   useLayoutEffect(() => {
      const container = containerRef?.current;
      const snapshot = scrollSnapshotRef.current;
      const itemsCount = items.length;
      const prevCount = restorationPrevLengthRef.current;

      if (container && snapshot && itemsCount > prevCount) {
         const newIndex = findItemIndex(items, keyExtractor, snapshot.firstVisibleKey);

         if (newIndex !== -1) {
            isRestoringScrollRef.current = true;
            const itemsAdded = newIndex - snapshot.firstVisibleIndex;
            const newOffset = calculateOffset(
               items,
               0,
               newIndex,
               itemHeightsRef,
               keyExtractor,
               getItemHeightFn,
               itemHeight,
            );
            const addedHeight = newOffset - snapshot.offsetToFirstVisible;
            const newScrollTop = snapshot.scrollTop + addedHeight;

            // In column-reverse (inverted) containers, the browser natively maintains
            // the scroll position relative to the bottom. If items were loaded at
            // the visual TOP (older history), skip manual adjustment.
            if (inverted && snapshot.direction === 'top') {
               clearScrollSnapshot();
               isRestoringScrollRef.current = false;
               return;
            }

            container.scrollTop = inverted ? -newScrollTop : newScrollTop;
            setVisibleRange({
               start: visibleRangeRef.current.start + itemsAdded,
               end: visibleRangeRef.current.end + itemsAdded,
            });

            requestAnimationFrame(() => {
               requestAnimationFrame(() => {
                  calculateVisibleRange(true);
                  isRestoringScrollRef.current = false;
               });
            });
            clearScrollSnapshot();
         } else {
            const itemsAddedFallback = itemsCount - snapshot.itemsLength;
            if (itemsAddedFallback > 0) {
               isRestoringScrollRef.current = true;
               const addedHeightFallback = calculateOffset(
                  items,
                  0,
                  itemsAddedFallback,
                  itemHeightsRef,
                  keyExtractor,
                  getItemHeightFn,
                  itemHeight,
               );
               const fallbackScrollTop = snapshot.scrollTop + addedHeightFallback;
               container.scrollTop = inverted ? -fallbackScrollTop : fallbackScrollTop;

               requestAnimationFrame(() => {
                  calculateVisibleRange(true);
                  isRestoringScrollRef.current = false;
               });
            }
            clearScrollSnapshot();
         }
      }

      restorationPrevLengthRef.current = itemsCount;
   }, [
      items,
      items.length,
      containerRef,
      itemHeight,
      getItemHeightFn,
      keyExtractor,
      calculateVisibleRange,
      clearScrollSnapshot,
      inverted,
   ]);

   // ─── Scroll Helpers ────────────────────────────────────────────────────────

   const calculateTargetMetrics = useCallback(
      targetIndex => {
         const offsetToTarget = calculateOffset(
            items,
            0,
            targetIndex,
            itemHeightsRef,
            keyExtractor,
            getItemHeightFn,
            itemHeight,
         );
         const targetHeight = getItemHeightValue(
            items[targetIndex],
            targetIndex,
            itemHeightsRef,
            keyExtractor,
            getItemHeightFn,
            itemHeight,
         );
         return { offsetToTarget, targetHeight };
      },
      [items, itemHeight, getItemHeightFn, keyExtractor],
   );

   // DOM-based scroll — reads actual screen coordinates so accumulated height
   // estimation errors across items never affect the final position.
   const performScrollByKey = useCallback(
      (key, index, block, smooth) => {
         const container = containerRef?.current;
         if (!container) return;

         const applyDOMScroll = el => {
            const localContainer = containerRef?.current;
            if (!localContainer) return 0;

            const containerRect = localContainer.getBoundingClientRect();
            const elementRect = el.getBoundingClientRect();
            const elementVisualTop = elementRect.top - containerRect.top;
            const elementHeight = elementRect.height;
            const containerHeight = localContainer.clientHeight;

            let targetVisualTop;
            if (typeof block === 'number') {
               const clamped = Math.min(1, Math.max(0, block));
               targetVisualTop = (containerHeight - elementHeight) * clamped;
            } else if (block === 'smart') {
               targetVisualTop = resolveSmartTargetVisualTop(elementVisualTop, elementHeight, containerHeight);
            } else {
               const map = {
                  center: (containerHeight - elementHeight) / 2,
                  start: 0,
                  end: containerHeight - elementHeight,
                  bottom: containerHeight - elementHeight,
               };
               targetVisualTop = map[block] ?? (containerHeight - elementHeight) / 2;
            }

            const adjustment = elementVisualTop - targetVisualTop;
            const newScrollTop = localContainer.scrollTop + adjustment;
            localContainer.scrollTo({ top: newScrollTop, behavior: smooth ? 'smooth' : 'auto' });
            localContainer.scrollTop = newScrollTop;
            return adjustment;
         };

         // ─── PRIMARY: element already in DOM ─────────────────────────────────
         const element = container.querySelector(`[data-key="${key}"]`);
         if (element) {
            applyDOMScroll(element);
            setTimeout(() => {
               const localContainer = containerRef?.current;
               if (!localContainer) return;
               const el2 = localContainer.querySelector(`[data-key="${key}"]`);
               if (el2) applyDOMScroll(el2);
            }, 300);
            return;
         }

         // ─── FALLBACK: element not yet in DOM ─────────────────────────────────
         const { offsetToTarget, targetHeight } = calculateTargetMetrics(index);
         const maxScroll = container.scrollHeight - container.clientHeight;
         const fallbackBlock = block === 'smart' || typeof block === 'number' ? 'center' : block;
         const finalPos = calculateScrollPosition(
            offsetToTarget,
            targetHeight,
            container.clientHeight,
            fallbackBlock,
            maxScroll,
         );
         const scrollPos = inverted ? -finalPos : finalPos;
         container.scrollTo({ top: scrollPos, behavior: smooth ? 'smooth' : 'auto' });
         container.scrollTop = scrollPos;

         setTimeout(() => {
            const localContainer = containerRef?.current;
            if (!localContainer) return;
            const el = localContainer.querySelector(`[data-key="${key}"]`);
            if (el) {
               applyDOMScroll(el);
            } else {
               const { offsetToTarget: newOff, targetHeight: newH } = calculateTargetMetrics(index);
               const newMax = localContainer.scrollHeight - localContainer.clientHeight;
               const newFinal = calculateScrollPosition(
                  newOff,
                  newH,
                  localContainer.clientHeight,
                  fallbackBlock,
                  newMax,
               );
               const newSP = inverted ? -newFinal : newFinal;
               if (Math.abs(localContainer.scrollTop - newSP) > 10) localContainer.scrollTop = newSP;
            }
         }, 300);
      },
      [containerRef, inverted, calculateTargetMetrics],
   );

   // Resolves with { success, key, index } once scroll settles (stable frames).
   // If a newer scrollToKey starts, this one cancels and resolves success:false.
   const handleScrollToCommon = useCallback(
      async (index, options = {}) => {
         const container = containerRef?.current;
         if (!container || index === -1) return { success: false, reason: 'no-container-or-index' };

         const { smooth = false, block = 'smart' } = options;
         const key = keyExtractor(items[index], index);

         targetIndexRef.current = index;
         setVisibleRange({
            start: Math.max(0, index - SCROLL_BUFFER),
            end: Math.min(items.length, index + SCROLL_BUFFER + 1),
         });

         isProgrammaticScrollRef.current = true;

         await waitForTimeout(0);
         await waitForAnimationFrame();
         await waitForAnimationFrame();
         await waitForTimeout(100);

         performScrollByKey(key, index, block, smooth);

         if (cancelActiveScrollRef.current) {
            cancelActiveScrollRef.current();
            cancelActiveScrollRef.current = null;
         }

         const STABLE_FRAMES = 3;
         const MAX_POLL_MS = 1000;
         const pollStart = performance.now();

         return new Promise(resolve => {
            let lastTop = null;
            let stableCount = 0;
            let rafId = null;
            let resolved = false;

            const cancel = () => {
               if (resolved) return;
               resolved = true;
               if (rafId) cancelAnimationFrame(rafId);
               resolve({ success: false, reason: 'cancelled', key, index });
            };

            const done = () => {
               if (resolved) return;
               resolved = true;
               cancelActiveScrollRef.current = null;
               if (rafId) cancelAnimationFrame(rafId);
               resolve({ success: true, key, index });
            };

            cancelActiveScrollRef.current = cancel;

            const poll = () => {
               if (resolved) return;
               if (performance.now() - pollStart > MAX_POLL_MS) {
                  done();
                  return;
               }

               const localContainer = containerRef?.current;
               if (!localContainer) {
                  done();
                  return;
               }

               const el = localContainer.querySelector(`[data-key="${key}"]`);
               if (!el) {
                  rafId = requestAnimationFrame(poll);
                  return;
               }

               const currentTop = el.getBoundingClientRect().top;
               if (currentTop === lastTop) {
                  stableCount++;
                  if (stableCount >= STABLE_FRAMES) {
                     done();
                     return;
                  }
               } else {
                  stableCount = 0;
                  lastTop = currentTop;
               }
               rafId = requestAnimationFrame(poll);
            };

            rafId = requestAnimationFrame(poll);

            if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
            scrollTimeoutRef.current = setTimeout(
               () => {
                  isProgrammaticScrollRef.current = false;
                  targetIndexRef.current = null;
                  calculateVisibleRange(true);
                  scrollTimeoutRef.current = null;
                  done();
               },
               smooth ? 800 : 400,
            );
         });
      },
      [items, containerRef, performScrollByKey, calculateVisibleRange, keyExtractor],
   );

   const scrollToKey = useCallback(
      (key, options = {}) => {
         const index = findItemIndex(items, keyExtractor, key);
         if (index !== -1) return handleScrollToCommon(index, options);
         return Promise.resolve({ success: false, reason: 'key-not-found', key });
      },
      [items, keyExtractor, handleScrollToCommon],
   );

   const scrollToIndex = useCallback(
      (index, options = {}) => {
         if (index >= 0 && index < items.length) return handleScrollToCommon(index, options);
         return Promise.resolve({ success: false, reason: 'index-out-of-bounds', index });
      },
      [items.length, handleScrollToCommon],
   );

   const jumpToBottom = useCallback(
      (options = {}) => {
         const container = containerRef?.current;
         if (!container || items.length === 0) return;

         if (scrollTimeoutRef.current) {
            clearTimeout(scrollTimeoutRef.current);
            scrollTimeoutRef.current = null;
         }
         targetIndexRef.current = null;
         isProgrammaticScrollRef.current = false;

         if (inverted) {
            container.scrollTop = 0;
            setVisibleRange({ start: 0, end: Math.min(overscan + SCROLL_BUFFER, items.length) });
            requestAnimationFrame(() => {
               if (options.smooth) container.scrollTo({ top: 0, behavior: 'smooth' });
               else container.scrollTop = 0;
               setTimeout(() => {
                  if (containerRef?.current) calculateVisibleRange(true);
               }, 50);
            });
         } else {
            container.scrollTop = container.scrollHeight;
            const bottomStart = Math.max(0, items.length - (overscan + SCROLL_BUFFER));
            setVisibleRange({ start: bottomStart, end: items.length });
            requestAnimationFrame(() => {
               if (options.smooth) container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
               else container.scrollTop = container.scrollHeight;
               setTimeout(() => {
                  if (containerRef?.current) calculateVisibleRange(true);
               }, 50);
            });
         }
      },
      [containerRef, calculateVisibleRange, items.length, overscan, inverted],
   );

   // ─── Items Length Change ───────────────────────────────────────────────────

   useEffect(() => {
      const currentLength = items.length;
      const prevLength = previousItemsLengthRef.current;
      const trackIndex = inverted ? 0 : currentLength - 1;
      const newKey = currentLength > 0 ? keyExtractor(items[trackIndex], trackIndex) : null;

      if (currentLength !== prevLength) calculateVisibleRange(true);

      lastTrackedKeyRef.current = newKey;
      previousItemsLengthRef.current = currentLength;
   }, [items, keyExtractor, inverted, calculateVisibleRange]);

   // ─── Initial Scroll ────────────────────────────────────────────────────────

   useEffect(() => {
      const container = containerRef?.current;
      const keyChanged = initialScrollKey !== lastInitializedScrollKeyRef.current;
      const indexChanged = initialScrollIndex !== null && initialScrollIndex !== lastInitializedScrollIndexRef.current;

      if (!container || (hasInitializedRef.current && !keyChanged && !indexChanged) || items.length === 0) return;

      if (initialScrollKey) {
         waitForAnimationFrame()
            .then(waitForAnimationFrame)
            .then(() => {
               scrollToKey(initialScrollKey, { block: initialScrollBlock });
               hasInitializedRef.current = true;
               lastInitializedScrollKeyRef.current = initialScrollKey;
               return waitForTimeout(READY_DELAY);
            })
            .then(() => setIsReady(true));
      } else if (initialScrollIndex !== null && initialScrollIndex >= 0 && initialScrollIndex < items.length) {
         waitForAnimationFrame()
            .then(waitForAnimationFrame)
            .then(() => {
               scrollToIndex(initialScrollIndex, { block: 'start' });
               hasInitializedRef.current = true;
               lastInitializedScrollIndexRef.current = initialScrollIndex;
               return waitForTimeout(READY_DELAY);
            })
            .then(() => setIsReady(true));
      } else if (inverted) {
         hasInitializedRef.current = true;
         setIsReady(true);
         calculateVisibleRange(true);
      } else {
         hasInitializedRef.current = true;
      }
   }, [
      items.length,
      inverted,
      initialScrollKey,
      initialScrollIndex,
      initialScrollBlock,
      containerRef,
      calculateVisibleRange,
      scrollToKey,
      scrollToIndex,
   ]);

   // ─── Offsets ───────────────────────────────────────────────────────────────

   const offsetTop = useMemo(() => {
      if (isRestoringScrollRef.current) return offsetTopRef.current;
      const offset = calculateOffset(
         items,
         0,
         visibleRange.start,
         itemHeightsRef,
         keyExtractor,
         getItemHeightFn,
         itemHeight,
      );
      offsetTopRef.current = offset;
      return offset;
   }, [visibleRange.start, items, itemHeight, getItemHeightFn, keyExtractor]);

   const offsetBottom = useMemo(() => {
      if (isRestoringScrollRef.current) return offsetBottomRef.current;
      const offset = calculateOffset(
         items,
         visibleRange.end,
         items.length,
         itemHeightsRef,
         keyExtractor,
         getItemHeightFn,
         itemHeight,
      );
      offsetBottomRef.current = offset;
      return offset;
   }, [visibleRange.end, items, itemHeight, getItemHeightFn, keyExtractor]);

   // ─── measureItem callback ──────────────────────────────────────────────────

   const measureItem = useCallback(
      (index, height, prevHeight = 0) => {
         const container = containerRef?.current;
         const item = items[index];
         if (!item || !container) return;

         const key = keyExtractor(item, index);

         const oldHeight = prevHeight || itemHeightsRef.current.get(key) || 0;

         if (oldHeight === height) return;

         itemHeightsRef.current.set(key, height);

         const delta = height - oldHeight;

         // 🔥 Calculate exact offset using your existing helper
         const itemOffset = calculateOffset(items, 0, index, itemHeightsRef, keyExtractor, getItemHeightFn, itemHeight);

         const scrollTop = inverted ? Math.abs(container.scrollTop) : container.scrollTop;

         // 🔥 ONLY adjust scroll if item is above viewport
         if (itemOffset < scrollTop) {
            const newScrollTop = scrollTop + delta;
            container.scrollTop = inverted ? -newScrollTop : newScrollTop;
         }

         // Keep your existing behavior
         if (Math.abs(delta) > 5) {
            calculateVisibleRange();
         }
      },
      [items, keyExtractor, calculateVisibleRange, containerRef, getItemHeightFn, itemHeight, inverted],
   );

   return {
      visibleRange,
      isReady,
      offsetTop,
      offsetBottom,
      measureItem,
      scrollToKey,
      scrollToIndex,
      jumpToBottom,
      takeScrollSnapshot,
      clearScrollSnapshot,
   };
};

export default useVirtualization;
